import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.MODE || 'development';
const RELEASE = import.meta.env.VITE_APP_VERSION || '0.1.0';

export function initSentry(): void {
  if (!SENTRY_DSN) {
    console.log('Sentry DSN not configured, error tracking disabled');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,
    release: `elevair-web@${RELEASE}`,
    integrations: [
      // Browser tracing
      Sentry.browserTracingIntegration(),
      // Replay for session recording (on errors only)
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance monitoring
    tracesSampleRate: ENVIRONMENT === 'production' ? 0.2 : 1.0,
    // Session replay - only capture on errors
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: ENVIRONMENT === 'production' ? 0.5 : 1.0,
    // Don't send PII
    sendDefaultPii: false,
    // Filter sensitive data
    beforeSend(event: Sentry.ErrorEvent) {
      // Remove sensitive URL params
      if (event.request?.url) {
        try {
          const url = new URL(event.request.url);
          url.searchParams.delete('token');
          url.searchParams.delete('code');
          event.request.url = url.toString();
        } catch {
          // URL parsing failed, leave as-is
        }
      }
      return event;
    },
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors
      'Network Error',
      'Failed to fetch',
      'Load failed',
      // User-initiated
      'AbortError',
      // Auth errors (expected)
      'TOKEN_EXPIRED',
      'UNAUTHORIZED',
    ],
    // Ignore errors from third-party scripts
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  console.log(`Sentry initialized for ${ENVIRONMENT} environment`);
}

// Set user context after login
export function setUserContext(user: {
  id: string;
  email?: string;
  organizationId?: string;
  tier?: string;
}): void {
  if (!SENTRY_DSN) return;

  Sentry.setUser({
    id: user.id,
    email: user.email,
    organizationId: user.organizationId,
    subscription_tier: user.tier,
  } as Sentry.User & { organizationId?: string; subscription_tier?: string });
}

// Clear user context on logout
export function clearUserContext(): void {
  if (!SENTRY_DSN) return;
  Sentry.setUser(null);
}

// Capture exception with context
export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (!SENTRY_DSN) return;

  if (context) {
    Sentry.withScope((scope: Sentry.Scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
}

// Add breadcrumb for debugging
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>
): void {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

// Export Sentry Error Boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export { Sentry };
